import { initVersionedSchema } from '@principle-theorem/editor';
import {
  AutomationCreator,
  AutomationParentScope,
  AutomationType,
  getGeneratedTaskFromConfiguration,
  IAutomation,
  IAutomationConfiguration,
  IAutomationResource,
  IAutomationScopeRequirements,
  IBrand,
  IEvent,
  IGeneratedTask,
  IGeneratedTaskConfiguration,
  IStaffer,
  ITask,
  ITeam,
  TaskPriority,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  DocumentReference,
  INamedDocument,
  INamedTypeDocument,
  isWithRef,
  Timestamp,
  WithRef,
} from '@principle-theorem/shared';
import { Automation } from '../automation/automation';
import { AutomationTiming } from '../automation/automation-timing';
import { Task } from './task';

export class GeneratedTask {
  static init(
    overrides: AtLeast<IGeneratedTask, 'timing' | 'scope'>
  ): IGeneratedTask {
    return {
      title: [],
      comment: initVersionedSchema(),
      isForTeam: false,
      priority: TaskPriority.Medium,
      ...overrides,
    };
  }

  static async toAutomation(
    task: IGeneratedTask | WithRef<IGeneratedTask>,
    creator: INamedTypeDocument<object, AutomationCreator>,
    event: IEvent,
    brandRef: DocumentReference<IBrand>,
    scopeData: IAutomationScopeRequirements,
    configRef?: DocumentReference<IAutomationConfiguration>
  ): Promise<IAutomation<IGeneratedTask>> {
    return Automation.init({
      type: AutomationType.GeneratedTask,
      data: task,
      parentScope: scopeData.parentScope,
      scopeData:
        scopeData.parentScope === AutomationParentScope.Patient
          ? scopeData
          : undefined,
      creator,
      triggerDate: await AutomationTiming.resolveTriggerDateFromEvent(
        event,
        task.timing
      ),
      brandRef,
      practiceRef: event.practice.ref,
      configRef,
    });
  }

  static async generateFromConfig(
    config: IGeneratedTaskConfiguration | WithRef<IGeneratedTaskConfiguration>,
    creator: INamedTypeDocument<object, AutomationCreator>,
    event: IEvent,
    brandRef: DocumentReference<IBrand>,
    scopeData: IAutomationScopeRequirements,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<IGeneratedTask>> {
    return Automation.init({
      type: AutomationType.GeneratedTask,
      data: getGeneratedTaskFromConfiguration(config),
      parentScope: scopeData.parentScope,
      scopeData:
        scopeData.parentScope === AutomationParentScope.Patient
          ? scopeData
          : undefined,
      creator,
      triggerDate: await AutomationTiming.resolveTriggerDateFromEvent(
        event,
        config.timing
      ),
      triggerAfterDate,
      brandRef,
      practiceRef: event.practice.ref,
      configRef: isWithRef(config) ? config.ref : undefined,
    });
  }

  static toTask(
    generatedTask: IGeneratedTask,
    automationSourceRef?: DocumentReference<IAutomation<IAutomationResource>>
  ): ITask {
    const assignment = generatedTask.isForTeam
      ? { assignedTeam: generatedTask.assignee as INamedDocument<ITeam> }
      : { assignedUser: generatedTask.assignee as INamedDocument<IStaffer> };
    return Task.init({
      ...generatedTask,
      ...assignment,
      automationSourceRef,
    });
  }
}
